import type { ReactNode } from "react";
import {
  AppstoreOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  LeftOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Grid } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuthRole } from "../../../utils/auth";
import dashboardLogo from "../../../assets/dashboard-logo.png";
import camberlyIcon from "../../../assets/camberly.png";
import {
  SidebarBackdrop,
  SidebarBrand,
  SidebarBrandIcon,
  SidebarCollapseButton,
  SidebarLogoImage,
  SidebarLogoRow,
  SidebarMenu,
  SidebarSectionLabel,
  SidebarSpacer,
  StyledSider,
} from "./style";

const { useBreakpoint } = Grid;

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

interface NavItem {
  key: string;
  icon: ReactNode;
  label: string;
  path?: string;
  adminOnly?: boolean;
  disabled?: boolean;
}

const menuItems: NavItem[] = [
  {
    key: "dashboard",
    icon: <AppstoreOutlined />,
    label: "Registration",
    path: "/dashboard",
  },
  {
    key: "appointments",
    icon: <CalendarOutlined />,
    label: "Appointments",
    path: "/appointments",
  },
  {
    key: "escalations",
    icon: <ExclamationCircleOutlined />,
    label: "Escalations",
    disabled: true,
  },
];

const managementItems: NavItem[] = [
  {
    key: "analytics",
    icon: <BarChartOutlined />,
    label: "Analytics",
    path: "/analytics",
  },
  {
    key: "admin",
    icon: <TeamOutlined />,
    label: "Admin Dashboard",
    path: "/admin",
    adminOnly: true,
  },
];

function filterByRole(items: NavItem[], adminUser: boolean): NavItem[] {
  return items.filter((i) => !i.adminOnly || adminUser);
}

function toMenuItems(items: NavItem[]) {
  return items.map(({ key, icon, label, disabled }) => ({
    key,
    icon,
    label,
    disabled,
  }));
}

export default function Sidebar({
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMobile = !screens.lg;
  const effectiveCollapsed = isMobile ? false : collapsed;

  const isAdminUser = getAuthRole() === "admin";
  const visibleManagementItems = filterByRole(managementItems, isAdminUser);
  const allItems = [...menuItems, ...visibleManagementItems];

  const selectedKey =
    allItems.find((item) => item.path && pathname.startsWith(item.path))?.key ??
    "";

  function handleMenuClick(key: string) {
    const item = allItems.find((i) => i.key === key);
    if (item?.path && item.path !== pathname) {
      navigate(item.path);
    }
    if (isMobile) onMobileClose();
  }

  return (
    <>
      <SidebarBackdrop $mobileOpen={mobileOpen} onClick={onMobileClose} />
      <StyledSider
        width={264}
        collapsedWidth={80}
        collapsed={effectiveCollapsed}
        trigger={null}
        $mobileOpen={mobileOpen}
      >
        <SidebarLogoRow
          align="center"
          justify="space-between"
          gap={14}
          $collapsed={effectiveCollapsed}
        >
          <SidebarBrand align="center" $collapsed={effectiveCollapsed}>
            {effectiveCollapsed ? (
              <SidebarBrandIcon
                src={camberlyIcon}
                alt="Camberley Dental Clinic"
                preview={false}
              />
            ) : (
              <SidebarLogoImage
                src={dashboardLogo}
                alt="Camberley Dental Clinic"
                preview={false}
              />
            )}
          </SidebarBrand>
          <SidebarCollapseButton
            type="text"
            icon={
              isMobile ? (
                <CloseOutlined />
              ) : (
                <LeftOutlined rotate={effectiveCollapsed ? 180 : 0} />
              )
            }
            onClick={isMobile ? onMobileClose : onToggleCollapse}
            aria-label={isMobile ? "Close menu" : "Toggle sidebar"}
          />
        </SidebarLogoRow>

        {!effectiveCollapsed && <SidebarSectionLabel>Menu</SidebarSectionLabel>}
        <SidebarMenu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={toMenuItems(menuItems)}
          onClick={({ key }) => handleMenuClick(String(key))}
        />

        {!effectiveCollapsed && (
          <SidebarSectionLabel>Management</SidebarSectionLabel>
        )}
        <SidebarMenu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={toMenuItems(visibleManagementItems)}
          onClick={({ key }) => handleMenuClick(String(key))}
        />

        <SidebarSpacer />
      </StyledSider>
    </>
  );
}
